import React from "react";
import { Button, Modal, Typography } from "antd";
import { LinkOutlined } from "@ant-design/icons";
import { mintAssignmentLink } from "../../../../../api/inventory/salesOrderApi";
import { useNotifications } from "../../../../../context/NotificationContext";
import { staffCan } from "../../../../../utils/userPrefs/validatePermissions";

// commerce declares the mint route perm:commerce.orders.write.
export const canCopyKitAssignmentLink = (): boolean => staffCan("commerce.orders.write");

interface CopyKitAssignmentLinkButtonProps {
	salesOrderId: number;
}

const CopyKitAssignmentLinkButton: React.FC<CopyKitAssignmentLinkButtonProps> = ({ salesOrderId }) => {
	const { showSuccess, showError } = useNotifications();

	if (!canCopyKitAssignmentLink()) return null;

	const mintAndCopy = async () => {
		let link: string;
		try {
			const { path } = await mintAssignmentLink(salesOrderId);
			link = window.location.origin + path;
		} catch {
			showError("Could not create the kit-assignment link");
			return;
		}
		try {
			await navigator.clipboard.writeText(link);
			showSuccess("Kit-assignment link copied");
		} catch {
			// The mint already revoked the earlier link, so a failed copy must not lose the new one.
			Modal.info({
				title: "Copy the kit-assignment link",
				content: <Typography.Paragraph copyable={{ text: link }} data-testid="sales-order-kit-link-fallback">{link}</Typography.Paragraph>,
			});
		}
	};

	const confirmAndCopy = () =>
		Modal.confirm({
			title: "Copy kit-assignment link",
			content: "This replaces any earlier link for this order.",
			okText: "Create and copy",
			onOk: mintAndCopy,
		});

	return (
		<Button icon={<LinkOutlined />} onClick={confirmAndCopy} data-testid="sales-order-kit-link-copy">
			Copy kit-assignment link
		</Button>
	);
};

export default CopyKitAssignmentLinkButton;
